import {
  isSketchArcObject,
  isSketchLineObject,
  type SketchArcObject,
  type SketchLineObject,
  type SketchPointObject,
} from '../../mesh/sketch';
import {SketchCornerMode} from './SketchCornerDrawingTypes';

/**
 * 草图圆角/倒角角点选择解析器。
 *
 * 职责：
 * 1. 把一个真实共享拓扑端点解析为两个既有源实体选择；
 * 2. 严格过滤圆心、中点、孤立点和多实体共点等非唯一角点；
 * 3. 保持点对象连接边快照的原始顺序，使点选与既有实体对输入语义一致。
 */
export class SketchCornerPointSelectionResolver implements SketchCornerPointSelectionResolverLike {
  /**
   * 解析一个角点对应的两个源实体选择。
   *
   * @param point 用户选择的草图点。
   * @param mode 当前草图圆角/倒角模式。
   * @returns 按点连接边快照原始顺序排列的两个源实体选择；当前点不是唯一可处理角点时返回 null。
   */
  public resolve(
    point: SketchPointObject,
    mode: SketchCornerMode,
  ): [SketchCornerSourceSelection, SketchCornerSourceSelection] | null {
    const sources = this.resolveSupportedEndpointSources(point);
    if (sources.length !== 2 || !this.isSupportedSourcePair(sources, mode)) {
      return null;
    }

    const pointWorld = point.getPosition();
    const hitPoint: [number, number, number] = [pointWorld.x, pointWorld.y, pointWorld.z];
    if (!hitPoint.every((value) => Number.isFinite(value))) {
      return null;
    }

    /**
     * `Map` 会保持连接边快照的首次插入顺序。
     * 此处只完成点到实体对的转换，禁止根据集合位置或持久 ID 重排 A、B，
     * 避免改变既有倒角距离 1、距离 2 对源实体的映射。
     */
    const [firstSource, secondSource] = sources;
    if (!firstSource || !secondSource) {
      return null;
    }
    return [
      {
        object: firstSource,
        hitPoint: [hitPoint[0], hitPoint[1], hitPoint[2]],
      },
      {
        object: secondSource,
        hitPoint: [hitPoint[0], hitPoint[1], hitPoint[2]],
      },
    ];
  }

  /**
   * 收集以当前点作为真实端点的受支持草图实体。
   *
   * @param point 用户选择的草图点。
   * @returns 去重后的直线或圆弧源实体。
   */
  private resolveSupportedEndpointSources(point: SketchPointObject): SketchFilletSourceObject[] {
    const sourceById = new Map<string, SketchFilletSourceObject>();
    point.getConnectedEdgesSnapshot().forEach((edge) => {
      if (!isSketchLineObject(edge) && !isSketchArcObject(edge)) {
        return;
      }
      if (!this.isSourceEndpoint(point, edge)) {
        return;
      }
      sourceById.set(edge.uuid, edge);
    });
    return [...sourceById.values()];
  }

  /**
   * 判断点是否为指定源实体的真实起点或终点。
   *
   * 设计说明：
   * `SketchArcObject` 会把圆心和中点也登记到连接边集合，因此不能只依赖
   * `getConnectedEdgesSnapshot()`，必须再次按端点对象身份收口。
   *
   * @param point 待判断草图点。
   * @param source 候选源实体。
   * @returns 当前点是源实体真实端点时返回 true。
   */
  private isSourceEndpoint(
    point: SketchPointObject,
    source: SketchLineObject | SketchArcObject,
  ): boolean {
    if (isSketchLineObject(source)) {
      return source.startPoint === point || source.endPoint === point;
    }
    return source.getStartPoint() === point || source.getEndPoint() === point;
  }

  /**
   * 判断实体组合是否被当前命令模式支持。
   *
   * @param sources 两个候选源实体。
   * @param mode 当前草图圆角/倒角模式。
   * @returns 当前组合可由既有几何链路处理时返回 true。
   */
  private isSupportedSourcePair(
    sources: readonly SketchFilletSourceObject[],
    mode: SketchCornerMode,
  ): boolean {
    const lineCount = sources.filter((source) => isSketchLineObject(source)).length;
    const arcCount = sources.filter((source) => isSketchArcObject(source)).length;
    if (mode === SketchCornerMode.Chamfer) {
      return lineCount === 2;
    }
    return lineCount === 2 || (lineCount === 1 && arcCount === 1);
  }
}
